import React, { useEffect, useMemo, useState } from 'react'
import Modal from '../../../../shared/ui/overlay/Modal/Modal'
import { Button } from '../../../../shared/ui/controls/Button/Button'
import { ControllingPdfColumn, ControllingActiveFilter } from '@myapp/contract'
import { ControllingFiltersForm, mapFiltersFormToApi } from '../../filters'
import {
  useGenerateControllingPdfMutation,
  useGenerateControllingXlsxMutation,
} from '../../../../entities/controlling'
import { buildActiveFilterItems } from '../model/buildActiveFilterItems'
import { EXPORT_COLUMNS } from '../model/controllingExport.constants'
import { ExportFilterChecklist } from './ExportFilterChecklist'
import { ExportColumnChecklist } from './ExportColumnChecklist'

export interface ControllingExportModalProps {
  show: boolean
  onClose: () => void
  filters: ControllingFiltersForm
  technicianLabel?: string | null
  tradeLabel?: string | null
  technicianTradeLabel?: string | null
}

export const ControllingExportModal: React.FC<ControllingExportModalProps> = ({
  show,
  onClose,
  filters,
  technicianLabel,
  tradeLabel,
  technicianTradeLabel,
}) => {
  const allFilterItems = useMemo(
    () => buildActiveFilterItems(filters, technicianLabel, tradeLabel, technicianTradeLabel),
    [filters, technicianLabel, tradeLabel, technicianTradeLabel],
  )

  const [checkedFilters, setCheckedFilters] = useState<Set<string>>(
    () => new Set(allFilterItems.map((f) => f.key)),
  )
  const [checkedColumns, setCheckedColumns] = useState<Set<ControllingPdfColumn>>(
    () => new Set(EXPORT_COLUMNS.map((c) => c.key)),
  )

  useEffect(() => {
    if (show) {
      setCheckedFilters(new Set(allFilterItems.map((f) => f.key)))
    }
  }, [show, allFilterItems])

  const { mutate: mutatePdf, isPending: isPdfPending, isError: isPdfError } = useGenerateControllingPdfMutation()
  const { mutate: mutateXlsx, isPending: isXlsxPending, isError: isXlsxError } = useGenerateControllingXlsxMutation()

  const toggleFilter = (key: string) => {
    setCheckedFilters((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const toggleColumn = (key: ControllingPdfColumn) => {
    setCheckedColumns((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const selectedColumns = EXPORT_COLUMNS.filter((c) => checkedColumns.has(c.key)).map((c) => c.key)
  const apiFilters = mapFiltersFormToApi(filters, 9999)

  const handlePdfSubmit = () => {
    if (selectedColumns.length === 0) return
    const activeFilters: ControllingActiveFilter[] = allFilterItems
      .filter((f) => checkedFilters.has(f.key))
      .map((f) => ({ label: f.label, value: f.value }))
    mutatePdf({ ...apiFilters, selectedColumns, activeFilters }, { onSuccess: onClose })
  }

  const handleXlsxSubmit = () => {
    if (selectedColumns.length === 0) return
    mutateXlsx({ ...apiFilters, selectedColumns }, { onSuccess: onClose })
  }

  const isDisabled = checkedColumns.size === 0

  return (
    <Modal
      show={show}
      setShow={(v) => !v && onClose()}
      title="Export"
      size="big"
      actionButtons={
        <div className="flex flex-col gap-2 w-full">
          <div className="flex gap-3 w-full">
            <Button
              onClick={handleXlsxSubmit}
              isLoading={isXlsxPending}
              disabled={isDisabled}
              fullWidth
              className="!bg-[#124093] !text-white !h-[50px] !rounded-[30px] !text-[20px] !font-bold !font-[PT_Sans]"
            >
              Excel erstellen
            </Button>
            <Button
              onClick={handlePdfSubmit}
              isLoading={isPdfPending}
              disabled={isDisabled}
              fullWidth
              className="!bg-[#124093] !text-white !h-[50px] !rounded-[30px] !text-[20px] !font-bold !font-[PT_Sans]"
            >
              PDF erstellen
            </Button>
          </div>
          {(isPdfError || isXlsxError) && (
            <p className="text-center text-sm text-red-500">
              Export fehlgeschlagen. Bitte versuche es erneut.
            </p>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:gap-6 sm:items-start sm:min-h-[340px] max-h-[70dvh] sm:max-h-[65dvh] overflow-y-auto pr-1">
        <ExportFilterChecklist
          items={allFilterItems}
          checked={checkedFilters}
          onToggle={toggleFilter}
        />
        <div className="hidden sm:block w-px bg-gray-200 self-stretch shrink-0" />
        <div className="block sm:hidden h-px bg-gray-200 w-full shrink-0" />
        <ExportColumnChecklist checked={checkedColumns} onToggle={toggleColumn} />
      </div>
    </Modal>
  )
}
